import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/Footer';
import { Mail, Phone, Clock, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-4">Get In Touch</h1>
            <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto">
              Have questions or ready to place an order? We'd love to hear from you!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 lg:p-12 border border-[var(--border-soft)]" data-testid="contact-form">
              <h2 className="text-2xl mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your Name"
                    data-testid="contact-name-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Phone (Optional)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(123) 456-7890"
                    data-testid="contact-phone-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Tell us about your inquiry or custom order..."
                    data-testid="contact-message-input"
                    rows={5}
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="w-full px-8 py-4 bg-[var(--accent-gold)] text-white rounded-full text-lg font-medium button-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info & FAQ */}
            <div className="space-y-8">
              {/* Contact Info */}
              <div className="bg-gradient-to-br from-[var(--background-secondary)] to-white rounded-3xl p-8 border border-[var(--border-soft)]" data-testid="contact-info">
                <h2 className="text-2xl mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Mail className="text-[var(--accent-gold)] mt-1" size={24} />
                    <div>
                      <div className="font-medium">Email</div>
                      <div className="text-[var(--text-muted)]">hello@pearlpastries.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="text-[var(--accent-gold)] mt-1" size={24} />
                    <div>
                      <div className="font-medium">Phone</div>
                      <div className="text-[var(--text-muted)]">(555) 123-4567</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="text-[var(--accent-gold)] mt-1" size={24} />
                    <div>
                      <div className="font-medium">Pickup Hours</div>
                      <div className="text-[var(--text-muted)] text-sm">
                        Tuesday - Saturday: 10am - 6pm<br />
                        Sunday: 11am - 4pm<br />
                        Monday: Closed
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="text-[var(--accent-gold)] mt-1" size={24} />
                    <div>
                      <div className="font-medium">Location</div>
                      <div className="text-[var(--text-muted)] text-sm">
                        Local pickup only<br />
                        Address provided upon order confirmation
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ */}
              <div className="bg-white rounded-3xl p-8 border border-[var(--border-soft)]" data-testid="faq-section">
                <h2 className="text-2xl mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">How far in advance should I order?</h3>
                    <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                      We recommend placing orders at least 1 week in advance for custom designs. 
                      Simple orders may be available with 3-4 days notice.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Do you deliver?</h3>
                    <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                      Currently, we offer local pickup only. Delivery options may be available for large orders.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Can you accommodate dietary restrictions?</h3>
                    <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                      Yes! Please note any allergies or dietary needs in your order, and we'll work with you to find suitable options.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">What's your cancellation policy?</h3>
                    <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                      Please notify us at least 48 hours before pickup for cancellations or changes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}